import type { PerspectiveCamera } from 'three';
import type { ControlState, ControlEnv, InputAdapter } from '../controls/types';
import type { MaterialFactory } from '../render/types';
import type { CameraPose } from '../data/types';
import type { Pose } from '../controls/types';
import { updateControls } from '../controls/controller';
import { createPainterProjectionState, painterFramingAmount } from './painterProjectionDriver';
import { applyPoseToCamera, computeFovScale } from './cameraRig';
import { updateWarpCulling, type WarpCulling, type WarpMesh } from './warpCulling';
import { WORLD } from '../core/config';
import { lerp } from '../core/math';
import { sceneSeconds } from './sceneClock';

export interface ControlRuntimeState {
  controlState: ControlState;
  warpCulling: WarpCulling;
}

export interface ControlRuntimeEnv {
  env: ControlEnv;
  readonly painterPose: Pose;
  readonly painterView: CameraPose;
  readonly camera: PerspectiveCamera;
  readonly materials: MaterialFactory;
  readonly warpMeshes: readonly WarpMesh[];
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly capturePainterView?: () => void;
}

export interface ControlRuntimeResult {
  readonly controlState: ControlState;
  readonly warpCulling: WarpCulling;
  readonly projectionAmount: number;
}

export function updateControlRuntime(
  state: ControlRuntimeState,
  input: { readonly frame: ReturnType<InputAdapter['poll']>['frame']; readonly dt: number },
  runtime: ControlRuntimeEnv,
  helpOpen: boolean,
  still: boolean
): ControlRuntimeResult {
  let controlState = state.controlState;

  if (!still && !helpOpen) {
    controlState = updateControls(controlState, input.frame, input.dt, runtime.env);
  }

  const projectionState = createPainterProjectionState(
    controlState.pose,
    runtime.painterPose,
    controlState.transition
  );
  const previousProjection = createPainterProjectionState(
    state.controlState.pose,
    runtime.painterPose,
    state.controlState.transition
  );
  if (
    (previousProjection.amount === 1 && projectionState.amount === 0) ||
    (previousProjection.amount === 0 && projectionState.amount === 1)
  )
    runtime.capturePainterView?.();
  const fovScale = lerp(
    1,
    computeFovScale(runtime.canvasWidth, runtime.canvasHeight, runtime.painterView),
    painterFramingAmount(controlState.pose, runtime.painterPose, controlState.transition)
  );

  applyPoseToCamera(controlState.pose, runtime.camera, WORLD.groundY, fovScale);

  const timeSeconds = sceneSeconds(still);
  runtime.materials.update(timeSeconds);

  runtime.materials.setPainterProjection(projectionState);

  const warpCulling = updateWarpCulling(
    runtime.warpMeshes,
    state.warpCulling,
    projectionState.amount > 0
  );

  return {
    controlState,
    warpCulling,
    projectionAmount: projectionState.amount,
  };
}

export function createEmptyFrame(): ReturnType<InputAdapter['poll']>['frame'] {
  return {
    forward: 0,
    right: 0,
    up: 0,
    lookDx: 0,
    lookDy: 0,
    wheelSteps: 0,
    boost: false,
    actions: [],
  };
}
